import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { useAuth } from "../lib/auth";
import { api, formatApiErrorDetail } from "../lib/api";
import { formatDate, formatBytes } from "../lib/format";
import { Shield, CheckCircle2, XCircle, Database, HardDrive, Plus, Trash2, FileText, Info, UserX, Search as SearchIcon } from "lucide-react";
import { toast, Toaster } from "sonner";

const CATEGORIES = [
  { key: "industry", label: "Industry" },
  { key: "domain", label: "Domain / Function" },
  { key: "status", label: "Candidate Status" },
  { key: "work_setup", label: "Work Setup" },
];

const ROLE_LABEL = {
  super_custodian: "Super Custodian",
  hod: "Head of Delivery",
  tl: "Team Lead",
  recruiter: "Recruiter",
};

export default function Settings() {
  const { user } = useAuth();
  const isSC = user?.role === "super_custodian";

  return (
    <Layout>
      <Toaster richColors position="top-right" />
      <div className="mb-6">
        <h1 className="font-display text-3xl font-bold text-slate-900">Settings</h1>
        <p className="text-slate-500 mt-1">Your account and integration status.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <Card className="p-6 border-slate-200" data-testid="settings-account">
          <h3 className="font-display font-semibold text-lg text-slate-900 mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-indigo-600" /> Account
          </h3>
          <dl className="space-y-3 text-sm">
            <div className="flex justify-between"><dt className="text-slate-500">Email</dt><dd className="font-medium text-slate-900">{user?.email}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Name</dt><dd className="font-medium text-slate-900">{user?.name || "—"}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Role</dt><dd className="font-medium text-slate-900">{ROLE_LABEL[user?.role] || user?.role}</dd></div>
            <div className="flex justify-between items-center">
              <dt className="text-slate-500">Google linked</dt>
              <dd>
                {user?.google_linked
                  ? <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><CheckCircle2 className="w-4 h-4" />Linked</span>
                  : <span className="inline-flex items-center gap-1 text-xs text-slate-400"><XCircle className="w-4 h-4" />Not linked</span>}
              </dd>
            </div>
          </dl>
        </Card>

        <Card className="p-6 border-slate-200">
          <h3 className="font-display font-semibold text-lg text-slate-900 mb-4">Session</h3>
          <p className="text-sm text-slate-500">Your session expires after 8 hours of inactivity. Use the Log out button in the sidebar to end your session.</p>
        </Card>
      </div>

      {isSC && <ManageDatabase />}
    </Layout>
  );
}

function ManageDatabase() {
  const [dropdowns, setDropdowns] = useState({ industry: [], domain: [], status: [], work_setup: [] });
  const [inputs, setInputs] = useState({});
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [d, i] = await Promise.all([api.get("/meta/dropdowns"), api.get("/meta/app-info")]);
      setDropdowns(d.data);
      setInfo(i.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const addOption = async (cat) => {
    const val = (inputs[cat] || "").trim();
    if (!val) return;
    try {
      await api.post("/meta/dropdowns", { category: cat, value: val });
      setInputs((s) => ({ ...s, [cat]: "" }));
      toast.success(`Added "${val}" to ${cat}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const removeOption = async (cat, id, val, confirmForced = false) => {
    try {
      const res = await api.delete(`/meta/dropdowns/${id}`, { params: confirmForced ? { confirm: true } : {} });
      if (res.data.warning) {
        const proceed = window.confirm(`"${val}" is in use by ${res.data.in_use_count} candidate(s). Remove anyway?`);
        if (proceed) return removeOption(cat, id, val, true);
        return;
      }
      toast.success(`Removed "${val}"`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteResume = async (candidateId, filename) => {
    if (!window.confirm(`Delete resume for candidate ${filename}?`)) return;
    try {
      await api.delete(`/candidates/${candidateId}/resume`);
      toast.success("Resume deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="manage-database">
      <div className="flex items-center gap-2">
        <Database className="w-5 h-5 text-indigo-600" />
        <h2 className="font-display font-bold text-xl text-slate-900">Manage Database</h2>
      </div>

      {/* App info */}
      <Card className="p-6 border-slate-200" data-testid="app-info-card">
        <h3 className="font-display font-semibold text-lg text-slate-900 mb-4 flex items-center gap-2">
          <Info className="w-4 h-4 text-indigo-600" /> App info
        </h3>
        {loading || !info ? <p className="text-sm text-slate-400">Loading…</p> : (
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <Stat label="App version" value={info.app_version} />
            <Stat label="Approved users" value={info.user_count} />
            <Stat label="Candidates" value={info.candidate_count.toLocaleString()} />
            <Stat label="Resume storage" value={formatBytes(info.resume_total_bytes)} sub={`${info.resume_file_count} file(s)`} />
          </dl>
        )}
      </Card>

      {/* Dropdown manager */}
      {CATEGORIES.map((cat) => (
        <Card key={cat.key} className="p-6 border-slate-200" data-testid={`dropdown-mgr-${cat.key}`}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display font-semibold text-slate-900">{cat.label} <span className="text-xs text-slate-400 font-normal">({(dropdowns[cat.key] || []).length})</span></h3>
          </div>
          <div className="flex flex-wrap gap-2 mb-3">
            {(dropdowns[cat.key] || []).map((o) => (
              <span key={o.id} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-slate-100 text-slate-700 rounded" data-testid={`dropdown-${cat.key}-${o.value}`}>
                {o.value}
                <button
                  data-testid={`remove-${cat.key}-${o.value}`}
                  onClick={() => removeOption(cat.key, o.id, o.value)}
                  className="hover:text-red-600"
                ><Trash2 className="w-3 h-3" /></button>
              </span>
            ))}
            {(dropdowns[cat.key] || []).length === 0 && <span className="text-xs text-slate-400">No options yet</span>}
          </div>
          <div className="flex gap-2">
            <Input
              data-testid={`add-${cat.key}-input`}
              value={inputs[cat.key] || ""}
              onChange={(e) => setInputs((s) => ({ ...s, [cat.key]: e.target.value }))}
              placeholder={`Add ${cat.label.toLowerCase()}…`}
              className="max-w-sm"
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addOption(cat.key); } }}
            />
            <Button data-testid={`add-${cat.key}-submit`} type="button" variant="outline" onClick={() => addOption(cat.key)}>
              <Plus className="w-4 h-4 mr-1" />Add
            </Button>
          </div>
        </Card>
      ))}

      {/* Delete candidates */}
      <DeleteCandidatesPanel />

      {/* Resume storage */}
      <Card className="p-6 border-slate-200" data-testid="resume-storage-card">
        <h3 className="font-display font-semibold text-lg text-slate-900 mb-4 flex items-center gap-2">
          <HardDrive className="w-4 h-4 text-indigo-600" /> Resume storage
        </h3>
        {loading || !info ? <p className="text-sm text-slate-400">Loading…</p> : info.resume_files.length === 0 ? (
          <p className="text-sm text-slate-400">No resumes uploaded yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-slate-500 text-xs uppercase tracking-wider">
                <tr className="border-b border-slate-100">
                  <th className="text-left py-2 font-semibold">Candidate</th>
                  <th className="text-left py-2 font-semibold">Filename</th>
                  <th className="text-right py-2 font-semibold">Size</th>
                  <th className="text-left py-2 font-semibold">Uploaded</th>
                  <th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {info.resume_files.map((f) => (
                  <tr key={f.candidate_id} className="border-b border-slate-100" data-testid={`resume-row-${f.candidate_id}`}>
                    <td className="py-2">
                      <Link to={`/candidate/${f.candidate_id}`} className="text-slate-900 hover:text-indigo-700 font-medium">{f.candidate_name}</Link>
                    </td>
                    <td className="py-2 text-slate-600 text-xs truncate max-w-xs">{f.filename}</td>
                    <td className="py-2 text-right text-slate-600 text-xs">{formatBytes(f.size_bytes)}</td>
                    <td className="py-2 text-slate-600 text-xs">{f.uploaded_at ? formatDate(f.uploaded_at) : "—"}</td>
                    <td className="py-2 text-right">
                      <div className="inline-flex gap-3">
                        <Link
                          to={`/candidate/${f.candidate_id}`}
                          className="text-xs text-indigo-600 hover:text-indigo-700 inline-flex items-center gap-1"
                          data-testid={`view-resume-${f.candidate_id}`}
                        ><FileText className="w-3.5 h-3.5" />View</Link>
                        <button
                          data-testid={`delete-resume-${f.candidate_id}`}
                          onClick={() => deleteResume(f.candidate_id, f.filename)}
                          className="text-xs text-red-600 hover:text-red-700 inline-flex items-center gap-1"
                        ><Trash2 className="w-3.5 h-3.5" />Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function DeleteCandidatesPanel() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [target, setTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const runSearch = async (e) => {
    if (e) e.preventDefault();
    const term = q.trim();
    if (!term) { setResults([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/candidates", { params: { q: term, limit: 20 } });
      setResults(data.items || []);
    } catch {
      toast.error("Search failed");
    } finally { setLoading(false); }
  };

  const confirmDelete = async () => {
    if (!target) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/candidates/${target.id}`);
      toast.success(`Deleted ${data.candidate_name}`);
      setResults((prev) => prev.filter((c) => c.id !== target.id));
      setTarget(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    } finally { setBusy(false); }
  };

  return (
    <Card className="p-6 border-slate-200" data-testid="delete-candidates-card">
      <h3 className="font-display font-semibold text-lg text-slate-900 mb-1 flex items-center gap-2">
        <UserX className="w-4 h-4 text-red-600" /> Delete candidates
      </h3>
      <p className="text-xs text-slate-500 mb-4">
        Permanently remove a candidate record along with all notes, history, and resume. This cannot be undone.
      </p>
      <form onSubmit={runSearch} className="flex gap-2 mb-4">
        <div className="relative flex-1 max-w-md">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            data-testid="delete-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or contact number…"
            className="pl-9"
          />
        </div>
        <Button data-testid="delete-search-submit" type="submit" variant="outline" disabled={loading || !q.trim()}>
          {loading ? "Searching…" : "Search"}
        </Button>
      </form>

      {results.length > 0 && (
        <div className="overflow-x-auto border border-slate-200 rounded-md">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left py-2 px-3 font-semibold">Name</th>
                <th className="text-left py-2 px-3 font-semibold">Contact</th>
                <th className="text-left py-2 px-3 font-semibold">Domain</th>
                <th className="text-left py-2 px-3 font-semibold">Logged by</th>
                <th className="text-right py-2 px-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {results.map((c) => (
                <tr key={c.id} className="border-t border-slate-100" data-testid={`delete-row-${c.id}`}>
                  <td className="py-2 px-3 font-medium text-slate-900">{c.candidate_name}</td>
                  <td className="py-2 px-3 text-slate-600">{c.contact_number || "—"}</td>
                  <td className="py-2 px-3 text-xs text-slate-600">{Array.isArray(c.domain) ? c.domain.join(", ") : (c.domain || "—")}</td>
                  <td className="py-2 px-3 text-xs text-slate-600">{c.logged_by || "—"}</td>
                  <td className="py-2 px-3 text-right">
                    <button
                      data-testid={`delete-btn-${c.id}`}
                      onClick={() => setTarget(c)}
                      className="text-xs text-red-600 hover:text-red-700 inline-flex items-center gap-1"
                    >
                      <Trash2 className="w-3.5 h-3.5" />Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!loading && q.trim() && results.length === 0 && (
        <p className="text-sm text-slate-400" data-testid="delete-no-results">No matching candidates.</p>
      )}

      <Dialog open={!!target} onOpenChange={(o) => !o && !busy && setTarget(null)}>
        <DialogContent data-testid="delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Permanently delete candidate?</DialogTitle>
            <DialogDescription>
              Are you sure you want to permanently delete <span className="font-semibold text-slate-900">{target?.candidate_name}</span>? This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTarget(null)} disabled={busy} data-testid="delete-cancel">Cancel</Button>
            <Button
              data-testid="delete-confirm"
              onClick={confirmDelete}
              disabled={busy}
              className="bg-red-600 hover:bg-red-700"
            >
              {busy ? "Deleting…" : "Confirm delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
      <div className="text-xl font-display font-bold text-slate-900 mt-1">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}
